import React, { MouseEvent, useState } from 'react';
import { observer } from 'mobx-react';
import colors from '@honeybook/hbui/dist/styles/colors';
import styled from 'styled-components';
import Clip16 from '@honeybook/icons/dist/icons/Clip16';
import Plus16 from '@honeybook/icons/dist/icons/Plus16';
import HBText from '@honeybook/hbui/dist/components/HBText/HBText';
import {
    TemplatesList,
    TemplatesListItem,
    TemplatesListItemContainer
} from '~/components/Automations/AutomationsBuilder/AutomationsBuilderSideBar/StepSideBar/StepSideBar.styles';
import PreviewEye from '~/components/Automations/AutomationsBuilder/AutomationsBuilderSideBar/Components/PreviewEye';
import TemplateSelectEmptyState from '~/components/Automations/AutomationsBuilder/AutomationsBuilderSideBar/SideBars/TemplateSelectEmptyState';
import { attachmentsCount } from '~/utils/automation.utils';
import { arraySorter } from '~/utils/sort.utils';
import { IVendorEmail } from '~/types/model.types';
import TemplatesListItemWrapper from '~/components/Automations/AutomationsBuilder/AutomationsBuilderSideBar/Components/TemplatesListItemWrapper';
import useAnalytics from '~/common/useAnalytics';
import { AnalyticsEvents } from '~/services/analytics/analytics-events';
import { useRootStore } from '~/stores/useRootStore';

interface Props {
    onClose: () => void;
}

const TemplateSelectSideBarEmail = ({ onClose }: Props) => {
    const {
        automationUiStore: { selectedStep, openEmailEditor },
        companyStore: { currCompany }
    } = useRootStore();

    const { track } = useAnalytics();

    const handleTemplateSelect = (id: string) => {
        selectedStep.selectEmailTemplate(id);
        onClose();
    };

    const previewEmail = (
        e: MouseEvent<HTMLElement>,
        template: IVendorEmail
    ) => {
        e.stopPropagation();
        openEmailEditor('previewFromList', template);
        track(AnalyticsEvents.AutomationPreviewEmail, {
            action: 'click',
            type: 'eye'
        });
    };

    const createNewEmailTemplate = () => {
        openEmailEditor('createNewTemplate');
        track(AnalyticsEvents.AutomationCreateNewEmailTemplate, {
            action: 'click'
        });
    };

    const sortedEmails = arraySorter(currCompany?.vendorEmails);

    const isSelected = (id: string) =>
        id === selectedStep?.email?._id && !selectedStep?.genericEmail;

    const [hoveredEye, setHoveredEye] = useState<string>(null);

    return (
        <TemplatesList>
            <TemplatesListItemContainer onClick={createNewEmailTemplate}>
                <PlusIcon color={colors.blue20} />
                <HBText type="emphasis16" color="blue20">
                    Create new email template
                </HBText>
            </TemplatesListItemContainer>
            {sortedEmails?.length ? (
                sortedEmails.map(template => (
                    <TemplatesListItemWrapper
                        isEmail
                        key={template._id}
                        isSelected={isSelected(template._id)}
                        isEyeHovered={hoveredEye === template._id}
                        onClick={() => handleTemplateSelect(template._id)}
                    >
                        <TemplatesListItem>{template.title}</TemplatesListItem>
                        {!!attachmentsCount(template) && (
                            <ClipIcon color={colors.neutral600} />
                        )}
                        <PreviewEye
                            onClick={e => previewEmail(e, template)}
                            onMouseEnter={() => setHoveredEye(template._id)}
                            onMouseLeave={() => setHoveredEye(null)}
                        />
                    </TemplatesListItemWrapper>
                ))
            ) : (
                <TemplateSelectEmptyState
                    type="email"
                    testIdName="email-empty-state"
                />
            )}
        </TemplatesList>
    );
};

export default observer(TemplateSelectSideBarEmail);

const PlusIcon = styled(Plus16)`
    display: flex;
`;

const ClipIcon = styled(Clip16)`
    display: flex;
`;
